import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VIEWPORT_MARGIN = 12;
const DEFAULT_OFFSET = 12;
const CLOSE_DELAY = 160;
const VALID_PLACEMENTS = new Set(['top', 'right', 'bottom', 'left']);
const BASE_TRIGGER_CLASS_NAME = 'inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-surface rounded-md';
const BASE_CONTENT_CLASS_NAME = 'fixed z-[12000] min-w-[220px] max-w-[320px] whitespace-normal rounded-2xl border border-line bg-surface-2 px-4 py-3 text-sm font-semibold text-content shadow-[0_12px_30px_rgba(0,0,0,0.45)] select-text';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getTransform = (placement) => {
  switch (placement) {
    case 'right':
      return 'translate(0, -50%)';
    case 'bottom':
      return 'translate(-50%, 0)';
    case 'left':
      return 'translate(-100%, -50%)';
    case 'top':
    default:
      return 'translate(-50%, -100%)';
  }
};

const resolvePlacement = (triggerRect, tooltipRect, preferredPlacement, offset) => {
  const fitsTop = triggerRect.top - offset - tooltipRect.height >= VIEWPORT_MARGIN;
  const fitsRight = triggerRect.right + offset + tooltipRect.width <= window.innerWidth - VIEWPORT_MARGIN;
  const fitsBottom = triggerRect.bottom + offset + tooltipRect.height <= window.innerHeight - VIEWPORT_MARGIN;
  const fitsLeft = triggerRect.left - offset - tooltipRect.width >= VIEWPORT_MARGIN;

  switch (preferredPlacement) {
    case 'top':
      return !fitsTop && fitsBottom ? 'bottom' : preferredPlacement;
    case 'right':
      return !fitsRight && fitsLeft ? 'left' : preferredPlacement;
    case 'bottom':
      return !fitsBottom && fitsTop ? 'top' : preferredPlacement;
    case 'left':
      return !fitsLeft && fitsRight ? 'right' : preferredPlacement;
    default:
      return preferredPlacement;
  }
};

const getPosition = (triggerRect, tooltipRect, preferredPlacement, offset) => {
  const placement = resolvePlacement(triggerRect, tooltipRect, preferredPlacement, offset);
  const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN);
  const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - tooltipRect.height - VIEWPORT_MARGIN);

  switch (placement) {
    case 'right':
      return {
        placement,
        left: clamp(triggerRect.right + offset, VIEWPORT_MARGIN, maxLeft),
        top: clamp(triggerRect.top + (triggerRect.height / 2), VIEWPORT_MARGIN + (tooltipRect.height / 2), window.innerHeight - VIEWPORT_MARGIN - (tooltipRect.height / 2)),
      };
    case 'bottom':
      return {
        placement,
        left: clamp(triggerRect.left + (triggerRect.width / 2), VIEWPORT_MARGIN + (tooltipRect.width / 2), window.innerWidth - VIEWPORT_MARGIN - (tooltipRect.width / 2)),
        top: clamp(triggerRect.bottom + offset, VIEWPORT_MARGIN, maxTop),
      };
    case 'left':
      return {
        placement,
        left: clamp(triggerRect.left - offset, VIEWPORT_MARGIN + tooltipRect.width, window.innerWidth - VIEWPORT_MARGIN),
        top: clamp(triggerRect.top + (triggerRect.height / 2), VIEWPORT_MARGIN + (tooltipRect.height / 2), window.innerHeight - VIEWPORT_MARGIN - (tooltipRect.height / 2)),
      };
    case 'top':
    default:
      return {
        placement,
        left: clamp(triggerRect.left + (triggerRect.width / 2), VIEWPORT_MARGIN + (tooltipRect.width / 2), window.innerWidth - VIEWPORT_MARGIN - (tooltipRect.width / 2)),
        top: clamp(triggerRect.top - offset, VIEWPORT_MARGIN + tooltipRect.height, window.innerHeight - VIEWPORT_MARGIN),
      };
  }
};

const Tooltip = ({
  children,
  content,
  disabled = false,
  disableOnMobile = false,
  placement = 'top',
  offset = DEFAULT_OFFSET,
  tabIndex,
  triggerClassName = '',
  contentClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  ));
  const [position, setPosition] = useState({ left: 0, top: 0, placement: 'top' });
  const closeTimeoutRef = useRef(null);
  const hoverStateRef = useRef({ trigger: false, tooltip: false });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipId = useId();
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const hasContent = content !== null && content !== undefined && content !== '';
  const isDisabled = disabled || (disableOnMobile && isMobile) || !hasContent;
  const normalizedPlacement = VALID_PLACEMENTS.has(placement) ? placement : 'top';

  useEffect(() => {
    if (!disableOnMobile || typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);

    updateIsMobile();
    mediaQuery.addEventListener('change', updateIsMobile);
    return () => mediaQuery.removeEventListener('change', updateIsMobile);
  }, [disableOnMobile]);

  useLayoutEffect(() => {
    if (!open || isDisabled || !triggerRef.current || !tooltipRef.current) {
      return undefined;
    }

    const updatePosition = () => {
      if (!triggerRef.current || !tooltipRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      setPosition(getPosition(triggerRect, tooltipRect, normalizedPlacement, offset));
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, isDisabled, normalizedPlacement, offset, content]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const showTooltip = () => {
    if (!isDisabled) {
      clearCloseTimeout();
      setOpen(true);
    }
  };

  const scheduleHideTooltip = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      if (!hoverStateRef.current.trigger && !hoverStateRef.current.tooltip) {
        setOpen(false);
      }
    }, CLOSE_DELAY);
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={`${BASE_TRIGGER_CLASS_NAME} ${triggerClassName}`.trim()}
        onMouseEnter={() => {
          hoverStateRef.current.trigger = true;
          showTooltip();
        }}
        onMouseLeave={() => {
          hoverStateRef.current.trigger = false;
          scheduleHideTooltip();
        }}
        onFocus={showTooltip}
        onBlur={scheduleHideTooltip}
        aria-describedby={open && !isDisabled ? tooltipId : undefined}
        tabIndex={tabIndex}
      >
        {children}
      </span>

      {portalTarget && open && !isDisabled && createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className={`${BASE_CONTENT_CLASS_NAME} ${contentClassName}`.trim()}
          onMouseEnter={() => {
            hoverStateRef.current.tooltip = true;
            clearCloseTimeout();
          }}
          onMouseLeave={() => {
            hoverStateRef.current.tooltip = false;
            scheduleHideTooltip();
          }}
          onMouseDown={clearCloseTimeout}
          style={{
            left: position.left,
            top: position.top,
            transform: getTransform(position.placement),
          }}
        >
          {content}
        </div>,
        portalTarget
      )}
    </>
  );
};

export default Tooltip;
